import { useEffect, useRef, useContext } from 'react'

import { StudyRegionContext } from './StudyRegionContext'
import { StudyTelemetryContext } from './StudyTelemetryContext'

import { GLOBAL_EVENTS } from './telemetryEvents'

// Study-side twin of component/workspace/context/MouseEventsContext.
// MOUSE_MOVE (50ms throttle), MOUSE_CLICK, TAB_STATE, WINDOW_RESIZE (250ms
// debounce) — identical payloads. The only change is the reference element:
// the workspace normalises against '.ws-content', the study IDE against the
// container ref supplied by StudyRegionProvider (the .chi-root box).
export const useStudyMouseEvents = () => {
  const { getRegionName, containerRef } = useContext(StudyRegionContext)
  const { logTelemetryEvent } = useContext(StudyTelemetryContext)

  const lastMouseLog = useRef(0)

  // Window resize listener
  useEffect(() => {
    let resizeTimeout = null

    const handleResize = () => {
      // Debounce to avoid spam during drag
      if (resizeTimeout) clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(() => {
        logTelemetryEvent(GLOBAL_EVENTS.WINDOW_RESIZE, {
          width: window.innerWidth,
          height: window.innerHeight,
          timestamp: Date.now()
        })
      }, 250)
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      if (resizeTimeout) clearTimeout(resizeTimeout)
    }
  }, [logTelemetryEvent])

  // Mouse and visibility listeners
  useEffect(() => {
    const normalize = (e) => {
      const container = containerRef?.current
      if (!container) return null

      const rect = container.getBoundingClientRect()
      if (!rect.width || !rect.height) return null

      return {
        x: Number(((e.clientX - rect.left) / rect.width).toFixed(4)),
        y: Number(((e.clientY - rect.top) / rect.height).toFixed(4))
      }
    }

    const handleMouseMove = (e) => {
      const now = Date.now()
      if (now - lastMouseLog.current > 50) {
        const point = normalize(e)
        if (!point) return

        logTelemetryEvent(GLOBAL_EVENTS.MOUSE_MOVE, {
          x: point.x,
          y: point.y,
          region: getRegionName(point.x, point.y)
        })
        lastMouseLog.current = now
      }
    }

    const handleClick = (e) => {
      const point = normalize(e)
      if (!point) return

      logTelemetryEvent(GLOBAL_EVENTS.MOUSE_CLICK, {
        x: point.x,
        y: point.y,
        tag: e.target.tagName,
        className: e.target.className,
        region: getRegionName(point.x, point.y)
      })
    }

    const handleVisibility = () => {
      logTelemetryEvent(GLOBAL_EVENTS.TAB_STATE, {
        visible: !document.hidden,
        timestamp: Date.now()
      })
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('click', handleClick)
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('click', handleClick)
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [logTelemetryEvent, getRegionName, containerRef])
}
